<script lang="ts" setup>
import SingleSelectInput from '@/corePagesModule/SelectInput/SingleSelectInput.vue';
import { i18n } from '@/i18n';
import { computed } from 'vue';
import TextInput from '@/unmanagedBusinessModule/components/TextInput.vue';
import { useForms } from '@/corePagesModule/composables/useForm';
import { FieldItem } from '@/coreDomainsModule/constant/validateRules';
import { ref, watch } from 'vue';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import AddOptionSection from './AddOptionSection.vue';
import { useUpdateBusinessModalStore } from '../useUpdateBusinessModalStore';
import { storeToRefs } from 'pinia';
import {
  formOptionsAggregateBusiness,
  inputTypeKeyMap,
  validateRuleKeyMap,
  keyMappingForSelectInput,
  unitOptions,
  unitOptionsMapping,
  formOptionsAggregateBusinessUniqueKeys,
} from '../constants';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { objectDiff } from '@/utils/objectDiff';
import { convertLeadTimeByUnit, convertLeadTimeToDays } from '@/coreDomainsModule/leadtime';
import MultipleUserSelectInput from '@/corePagesModule/user/SelectInput/MultipleUserSelectInput.vue';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';
import { UserAccount } from '@/coreDomainsModule/entities/users';
import { useUpdateFieldState } from '../composables/useUpdateFieldState';
import { UpdateBusinessViewModel } from '../UpdateBusinessViewModel';

const t = i18n.t;
const updateBusinessVM = new UpdateBusinessViewModel();
const store = useUpdateBusinessModalStore();
const { state } = storeToRefs(store);

const { getAddableOptions } = store;
const props = withDefaults(
  defineProps<{
    item?: IBusiness;
  }>(),
  { item: undefined }
);

const emits = defineEmits<{
  (e: 'close', modal: string, isNext?: boolean): void;
}>();

const isEdited = ref<boolean>(false);

const fieldItems = computed((): FieldItem[] => {
  const targetArray = formOptionsAggregateBusiness;
  const items = targetArray.map((name: string) => {
    return {
      name,
      rules: validateRuleKeyMap[name],
    };
  });
  return items;
});

const { fieldState, allValidate, resetForm } = useForms(fieldItems.value);
const { addNewOption, updateValue, updateUserValue } = useUpdateFieldState(fieldState, isEdited);

const currentOpenSelectInputName = ref('');

/**
 * 編集モーダルの保存ボタン押下時の処理
 */
const editingModalOnNext = async () => {
  if (allValidate()) {
    const aggregateBusinessResponsibleUsersString = fieldState['aggregateBusinessResponsibleUser'].value.value;
    const convertedLeadTimeUnit = updateBusinessVM.convertDateUnit(fieldState['leadTimeUnit'].value.value);
    const updatedItem = BusinessFactory.create({
      ...props.item,
      warehouseNumber: fieldState['warehouseNumber'].value.value,
      warehouse: fieldState['warehouseName'].value.value,
      supplierNumber: fieldState['supplierNumber'].value.value,
      supplierName: fieldState['supplierName'].value.value,
      businessNumber: fieldState['businessNumber'].value.value,
      businessName: fieldState['businessName'].value.value,
      package: fieldState['package'].value.value,
      unit: fieldState['unit'].value.value,
      manufacturer: updateBusinessVM.convertUnassingedToUndefined(fieldState['manufacturer'].value.value),
      leadTime: convertLeadTimeToDays(Number(fieldState['leadTime'].value.value) || 0, convertedLeadTimeUnit),
      leadTimeUnit: convertedLeadTimeUnit,
      MOQ: Number(fieldState['MOQ'].value.value) || 0,
      orderPackage: Number(fieldState['orderPackage'].value.value) || 0,
      stdInventoryIndices: {
        標準在庫指数: {
          max: Number(fieldState['stdInventoryIndicesMax'].value.value) || 0,
          min: Number(fieldState['stdInventoryIndicesMin'].value.value) || 0,
          option: {
            unit: updateBusinessVM.convertDateUnit(fieldState['stdInventoryIndicesUnit'].value.value),
          },
        },
      },
      userUniqueNumber: fieldState.userUniqueNumber.value.value,
      aggregateBusinessResponsibleUsers:
        aggregateBusinessResponsibleUsersString.length > 0
          ? aggregateBusinessResponsibleUsersString.split(',').map((userId) => UserAccountFactory.create({ email: userId }))
          : [],
    });
    const updatedDiff = objectDiff(props.item ?? {}, updatedItem);
    state.value.updatedItemDiff = updatedDiff.first;

    state.value.item = updatedItem;
    emits('close', 'aggregateBusinessEdit', true);
  }
};

/**
 * フォームのリストを見た目上で分かれている部分で分割して取得する
 */
const getDividedFormOptions = computed(() => {
  return [formOptionsAggregateBusiness.slice(0, 11), formOptionsAggregateBusiness.slice(11)];
});

/**
 * モーダルを開いた際の処理
 */
watch(
  () => state.value.modalState.aggregateBusinessEdit,
  (newVal) => {
    if (newVal && props.item) {
      const targetLeadTimeUnit = state.value.item?.['leadTimeUnit'] ?? props.item['leadTimeUnit'];
      const targetStdInventoryIndices = state.value.item?.stdInventoryIndices ?? props.item.stdInventoryIndices;
      resetForm(fieldItems.value);
      for (let i = 0; i < fieldItems.value.length; i++) {
        const keyName = fieldItems.value[i].name;
        switch (keyName) {
          case 'userUniqueNumber':
            fieldState[keyName].setValue(props.item.userUniqueNumber ?? '', false);
            break;

          case 'warehouseName':
            fieldState[keyName].setValue(state.value.item?.warehouse ?? props.item.warehouse, false);
            break;

          case 'leadTime':
            fieldState[keyName].setValue(
              convertLeadTimeByUnit(state.value.item?.leadTime ?? props.item.leadTime ?? 0, targetLeadTimeUnit ?? '').toString()
            );
            break;

          case 'leadTimeUnit':
            fieldState[keyName].setValue(targetLeadTimeUnit ? t(unitOptionsMapping[targetLeadTimeUnit]) : '', false);
            break;

          case 'stdInventoryIndicesMin':
            fieldState[keyName].setValue(
              targetStdInventoryIndices?.標準在庫指数?.min ? targetStdInventoryIndices['標準在庫指数'].min.toString() : '',
              false
            );
            break;

          case 'stdInventoryIndicesMax':
            fieldState[keyName].setValue(
              targetStdInventoryIndices?.標準在庫指数?.max ? targetStdInventoryIndices['標準在庫指数'].max.toString() : '',
              false
            );
            break;

          case 'stdInventoryIndicesUnit':
            fieldState[keyName].setValue(
              targetStdInventoryIndices?.標準在庫指数?.option.unit
                ? t(unitOptionsMapping[targetStdInventoryIndices['標準在庫指数'].option.unit])
                : '',
              false
            );
            break;

          case 'aggregateBusinessResponsibleUser':
            fieldState[keyName].setValue(
              state.value.item?.aggregateBusinessResponsibleUsers?.map((user: UserAccount) => user.email).join(',') ??
                props.item.aggregateBusinessResponsibleUsers?.map((user: UserAccount) => user.email).join(',') ??
                '',
              false
            );
            break;

          default:
            fieldState[keyName].setValue(state.value.item?.[keyName] ?? props.item[keyName] ?? '', false);
        }
      }
    }
  }
);

/**
 * inputのgrid幅指定クラス取得
 * @param option - inputの対応名
 * @param index - インデックス
 */
const getBaseDataItemClass = (option: string, index: number) => {
  if (index < 5) {
    return 'modal__item--base-data--upper';
  } else if (option === 'package' || option === 'unit') {
    return 'modal__item--base-data--number';
  } else {
    return 'modal__item--base-data--lower';
  }
};

const modalText = computed<{ title: string; desc: string }>(() => {
  if (state.value.isCreatingBoth) {
    return {
      title: t('追加する集計取引情報'),
      desc: t('関連取引と同時に追加する集計取引情報を入力してください。'),
    };
  } else if (state.value.isCreateMode) {
    return {
      title: t('取引追加'),
      desc: t('新規に取引を追加できます。'),
    };
  } else {
    return {
      title: t('取引編集'),
      desc: t('選択済の取引ファイルに対して、項目の内容を修正できます。'),
    };
  }
});
</script>
<template>
  <InfoModal
    :modal-state="state.modalState.aggregateBusinessEdit"
    :modal-width="1300"
    :back-button-label="t('キャンセル')"
    :next-button-label="t('保存')"
    :is-disabled-next-button="!isEdited"
    @on-back="
      () => {
        emits('close', 'aggregateBusinessEdit', false);
      }
    "
    @on-next="editingModalOnNext"
  >
    <template #title>{{ modalText.title }}</template>
    <template #description>{{ modalText.desc }}</template>
    <template #default>
      <div class="modal__container modal__container--base-data">
        <template v-for="(option, index) in getDividedFormOptions[0]" :key="option">
          <div class="modal__item" :class="getBaseDataItemClass(option, index)">
            <template v-if="inputTypeKeyMap[option] === 'select'">
              <SingleSelectInput
                :index="index"
                :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                :model-value="fieldState[option].value.value"
                :placeholder="t(option)"
                :filter-type="keyMappingForSelectInput[option]"
                :is-error="fieldState[option]?.isError.value"
                :error-message="fieldState[option]?.errorMessage.value"
                :disabled="state.isCreatingBoth && formOptionsAggregateBusinessUniqueKeys.includes(option)"
                @click:select-input="() => (currentOpenSelectInputName = option)"
                @update:model-value="(e) => updateValue(option, e)"
              />
            </template>
            <template v-else-if="inputTypeKeyMap[option] === 'selectUser'">
              <MultipleUserSelectInput
                :select-input-index="index"
                :label="t(option)"
                :placeholder="t(option)"
                :original-selected-user-id-list="fieldState[option].value.value ? fieldState[option].value.value.split(',') : []"
                @update:model-value="(e) => updateUserValue(option, e)"
              />
            </template>
            <template v-else>
              <TextInput
                :model-value="fieldState[option].value.value"
                :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                :placeholder="t(option)"
                :is-error="fieldState[option].isError.value"
                :error-message="t(fieldState[option].errorMessage.value)"
                ensure-error-msg-height
                @update:model-value="(e) => updateValue(option, e)"
              />
            </template>
          </div>
        </template>
      </div>
      <div class="modal__container modal__container--amount">
        <template v-for="(option, index) in getDividedFormOptions[1]" :key="option">
          <div class="modal__item">
            <template v-if="inputTypeKeyMap[option] === 'select'">
              <SingleSelectInput
                :index="index + getDividedFormOptions[0].length"
                :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                :model-value="fieldState[option].value.value"
                :placeholder="t(option)"
                :filter-type="keyMappingForSelectInput[option]"
                :is-error="fieldState[option]?.isError.value"
                :error-message="fieldState[option]?.errorMessage.value"
                @click:select-input="() => (currentOpenSelectInputName = option)"
                @update:model-value="(e) => updateValue(option, e)"
              />
            </template>
            <template v-if="inputTypeKeyMap[option] === 'selectUnit'">
              <SingleSelectInput
                :index="index + getDividedFormOptions[0].length"
                :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                :model-value="fieldState[option].value.value"
                :placeholder="t(option)"
                :filter-type="keyMappingForSelectInput[option]"
                :is-error="fieldState[option]?.isError.value"
                :error-message="fieldState[option]?.errorMessage.value"
                :prop-options="unitOptions.map((o) => t(o))"
                @click:select-input="() => (currentOpenSelectInputName = option)"
                @update:model-value="(e) => updateValue(option, e)"
              />
            </template>
            <template v-else>
              <TextInput
                :model-value="fieldState[option].value.value"
                :label="option === 'MOQ' ? t('取引編集MOQ') : t(option) + updateBusinessVM.getRequiredMark(option)"
                :placeholder="t(option)"
                :is-error="fieldState[option].isError.value"
                :error-message="t(fieldState[option].errorMessage.value)"
                ensure-error-msg-height
                @update:model-value="(e) => updateValue(option, e)"
              />
            </template>
          </div>
        </template>
      </div>
      <!-- 新規選択肢追加 -->
      <AddOptionSection :options="getAddableOptions(true)" @set-option="addNewOption" />
    </template>
  </InfoModal>
</template>
<style lang="scss" scoped>
.modal {
  &__container {
    display: grid;
    width: 100%;

    padding: 16px;
    border-radius: 8px;
    column-gap: 8px;
    row-gap: 16px;

    background-color: $backgroundDefault;

    &--base-data {
      grid-template-columns: repeat(20, 1fr);
    }

    &--amount {
      grid-template-columns: repeat(7, 1fr);
    }
  }

  &__item {
    display: grid;
    grid-template-columns: 100%;
    height: fit-content;

    box-sizing: border-box;
    border-bottom: solid 1px $borderDefault;

    &--base-data {
      &--upper {
        grid-column: span 5;
      }

      &--lower {
        grid-column: span 4;
      }

      &--number {
        grid-column: span 2;
      }
    }
  }
}
</style>
